import { useCallback, useEffect, useMemo, useState } from "react";
import { dayMs, minuteMs } from "verdi-common/timeConstants";
import { generateUID } from "verdi-common/utils/HelperFunctions";
import { doTick, HashedDaySchedule, MasterIndex, RepeatingIrrigationEvent } from "verdi-web-client/Models";
import type { IrrigationEventProto } from "verdi-web-client/Models/Scheduling/IrrigationEventProto";

const DEFAULT_FUTURE_DAYS_TO_SEARCH = 2;
const DEFAULT_PREVIOUS_DAYS_TO_SEARCH = 2;

const POLLING_INTERVAL = minuteMs * 5;

/**
 * Process the hashed day schedule range (array of HashedDaySchedule) and return all events.
 * Returned events are sorted by start time, earliest to latest.
 */
const processHashedDayScheduleRange = (
    hashedDayScheduleNodes: HashedDaySchedule[],
    recentlyEndedEventThresholdMs: number,
) => {
    const allIncludedEvents = new Set<IrrigationEventProto>();

    // Flatten to single set of included events
    hashedDayScheduleNodes.forEach((node) => {
        node.includedEvents.forEach((e) => {
            allIncludedEvents.add(e);
        });
    });

    // Remove events that have ended after the recentlyEndedEventThresholdMs and sort the remaining events by start time, sort earliest to latest
    const processedEvents = Array.from(allIncludedEvents)
        .filter((e) => {
            if (e.endTime.getTime() > Date.now() - recentlyEndedEventThresholdMs) {
                return true;
            }
            return false;
        })
        .sort((a, b) => a.startTime.getTime() - b.startTime.getTime());

    return {
        allEvents: processedEvents,
    };
};

/**
 * Returns the RepeatingIrrigationEvents for the given events
 */
const getAttachedRepeatingIrrigationEvents = (
    events: IrrigationEventProto[],
): (RepeatingIrrigationEvent | undefined)[] =>
    // @ts-expect-error event.commandGroup exists, but is not typed
    events.map((event) => MasterIndex.repeatingEvent.byID[event.commandGroup.id] || undefined);

/**
 * Returns the hashed day schedule nodes for the zone
 */
const getHashedDayScheduleNodes = ({
    zoneId,
    previousDaysToSearch,
    futureDaysToSearch,
}: {
    zoneId: string;
    previousDaysToSearch: number;
    futureDaysToSearch: number;
}) => {
    const startDate = new Date(Date.now() - previousDaysToSearch * dayMs);
    const newHashedDayScheduleNodes = new Set<HashedDaySchedule>();

    // For each day in the range, get all the events for that day and add them to the allEvents set
    for (let index = 0; index < futureDaysToSearch + previousDaysToSearch; index++) {
        const date = new Date(startDate.getTime() + index * dayMs);
        const foundScheduleNode = HashedDaySchedule.getHashRecord(date, zoneId);

        newHashedDayScheduleNodes.add(foundScheduleNode);
    }

    return Array.from(newHashedDayScheduleNodes);
};

interface UseUpcomingIrrigationEventParams {
    zoneId: string;
    futureDaysToSearch?: number;
    previousDaysToSearch?: number;
    recentlyEndedEventThresholdMs?: number; // time in ms since event ended to include in the search
    maxFutureMs?: number; // if set, only return events whose startTime is within this many ms from now
}
export const useUpcomingZoneIrrigationEvents = ({
    zoneId,
    futureDaysToSearch = DEFAULT_FUTURE_DAYS_TO_SEARCH,
    previousDaysToSearch = DEFAULT_PREVIOUS_DAYS_TO_SEARCH,
    recentlyEndedEventThresholdMs = minuteMs * 30,
    maxFutureMs,
}: UseUpcomingIrrigationEventParams) => {
    const [hashedDayScheduleNodes, setHashedDayScheduleNodes] = useState<HashedDaySchedule[]>([]);

    // On change, update the state with the new hashed day schedule node
    const onScheduleChange = useCallback(
        (
            _addedEvents: Set<IrrigationEventProto>,
            _changedEvents: Set<IrrigationEventProto>,
            _removedEvents: Set<IrrigationEventProto>,
            _newScheduleNode: HashedDaySchedule,
        ) => {
            // These scheduleNodes are mutable so this is effectively a dummy re-render
            setHashedDayScheduleNodes((prevNodes: HashedDaySchedule[]) => [...prevNodes]);
        },
        [],
    );

    const updateScheduleNodes = useCallback(() => {
        // This tick function updates the hashed day schedule nodes
        doTick();
    }, []);

    // Attach event listeners to the hashed day schedule nodes
    useEffect(() => {
        const nodeEventKeys: Record<string, string> = {};

        if (hashedDayScheduleNodes) {
            hashedDayScheduleNodes.forEach((node) => {
                const eventId = generateUID();

                node.addChangeListener((...args) => {
                    onScheduleChange(...args);
                }, eventId);

                nodeEventKeys[node.hashString] = eventId;
            });
        }

        // Clean up event listeners when the component unmounts
        return () => {
            hashedDayScheduleNodes?.forEach((node) => {
                if (nodeEventKeys[node.hashString]) {
                    node.removeListener(nodeEventKeys[node.hashString]);
                }
            });
        };
    }, [hashedDayScheduleNodes, onScheduleChange]);

    useEffect(() => {
        const createListenerId = generateUID();

        RepeatingIrrigationEvent.onNewRepeatingIrrigationEvent.addListener(updateScheduleNodes, createListenerId);

        return () => {
            RepeatingIrrigationEvent.onNewRepeatingIrrigationEvent.removeListener(createListenerId);
        };
    }, [updateScheduleNodes]);

    /**
     * Get references to the matching RepeatingIrrigationEvents and attach change listeners to them
     *
     * Make sure we "tick" the schedule when the RepeatingIrrigationEvents change in order to
     * trigger updates to the event listners in the hashed day schedule nodes
     */
    useEffect(() => {
        const { allEvents } = processHashedDayScheduleRange(hashedDayScheduleNodes, recentlyEndedEventThresholdMs);
        const attachedRepeatingIrrigationEvents = getAttachedRepeatingIrrigationEvents(allEvents);

        const listenerIds: Record<string, string> = {};
        attachedRepeatingIrrigationEvents.forEach((event) => {
            // event might be undefined if it was deleted
            if (event === undefined) {
                return;
            }
            const listenerId = generateUID();
            listenerIds[event.id] = listenerId;
            event.onChange.addListener(updateScheduleNodes, listenerId);
            event.onDelete.addListener(updateScheduleNodes, listenerId);
        });

        return () => {
            attachedRepeatingIrrigationEvents.forEach((event) => {
                if (event === undefined) {
                    return;
                }
                if (listenerIds[event.id]) {
                    // event might be undefined if it was deleted
                    event?.onChange.removeListener(listenerIds[event.id]);
                    event?.onDelete.removeListener(listenerIds[event.id]);
                }
            });
        };
    }, [hashedDayScheduleNodes, recentlyEndedEventThresholdMs, updateScheduleNodes]);

    /**
     * Get the hashed day schedule nodes and set up a polling interval to get the latest nodes
     */
    useEffect(() => {
        const nodes = getHashedDayScheduleNodes({ zoneId, previousDaysToSearch, futureDaysToSearch });
        setHashedDayScheduleNodes(nodes);

        const interval = setInterval(() => {
            const newNodes = getHashedDayScheduleNodes({ zoneId, previousDaysToSearch, futureDaysToSearch });
            setHashedDayScheduleNodes(newNodes);
        }, POLLING_INTERVAL);

        return () => clearInterval(interval);
    }, [zoneId, previousDaysToSearch, futureDaysToSearch]);

    const { allEvents } = useMemo(() => {
        // Events are already sorted by start time
        const { allEvents: eventsSortedByStartTime } = processHashedDayScheduleRange(
            hashedDayScheduleNodes,
            recentlyEndedEventThresholdMs,
        );
        const now = Date.now();

        // Optionally filter to events within maxFutureMs of now
        const filteredEvents =
            maxFutureMs !== undefined
                ? eventsSortedByStartTime.filter((e) => e.startTime.getTime() < now + maxFutureMs)
                : eventsSortedByStartTime;
        return { allEvents: filteredEvents };
    }, [hashedDayScheduleNodes, recentlyEndedEventThresholdMs, maxFutureMs]);

    return { allEvents };
};
